import { useTraderCommissionStats } from '../hooks/useTraderCommissionStats';
import { traderDateKey } from '../lib/traderTime';
import { useTraderTime } from '../contexts/TraderTimeContext';
import { formatCardNumber } from '../lib/utils';
import { WithdrawReceiptDownload } from '../components/WithdrawReceiptDownload';
import { bankDisplayName } from '../lib/kgsBanks';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { paymentsAPI, PaymentTransactionResponse } from '../lib/api';
import { formatAmountWithSeparator, formatAmount, getExchangeRate } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Pagination } from '../components/ui/pagination';
import { Loader2, ExternalLink, CheckCircle, Clock, Calendar, ArrowUpDown, RefreshCw, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { buildTransactionExportSheet } from '../lib/transactionExport';

const getRequisiteTypeLabel = (type?: string | null) => {
    switch (type) {
        case 'sbp': return 'СБП';
        case 'sbp_kgs': return 'СБП KGS';
        case 'card': return 'Банковская карта';
        case 'sbp_qr': return 'СБП QR';
        case 'mobile_topup': return 'Мобильное пополнение';
        case 'mobile_kgs': return 'Мобильный платёж';
        case 'mobile_tjs': return 'Mobile Commerce TJS';
        case 'qr_vietnam_sber': return 'QR Vietnam — Sber';
        case 'qr_vietnam_vtb': return 'QR Vietnam — VTB';
        case 'psb_ecom': return 'PSB E-com';
        case 'transgran_card_rub_arm': return 'transgran_card_rub_arm';
        case 'transgran_mobile_rub_arm': return 'transgran_mobile_rub_arm';
        case 'elqr': return 'ElQR';
        default: return type || 'Не указан';
    }
};

export const HistoryPage: React.FC = () => {
    const { formatDate, label, offsetMinutes } = useTraderTime();
    const [transactions, setTransactions] = useState<PaymentTransactionResponse[]>([]);
    const commissionStats = useTraderCommissionStats();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const exportingRef = useRef(false);
    const [exportProgress, setExportProgress] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const [transactionIdInput, setTransactionIdInput] = useState('');
    const [transactionId, setTransactionId] = useState('');
    const [requisitesInput, setRequisitesInput] = useState('');
    const [requisitesSearch, setRequisitesSearch] = useState('');
    const requestVersion = useRef(0);
    const latestOffset = useRef(offsetMinutes);
    latestOffset.current = offsetMinutes;

    const filters = useMemo(() => ({
        start_date: dateFrom || undefined,
        end_date: dateTo || undefined,
        order_type: typeFilter !== 'all' ? typeFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        requisites_search: requisitesSearch || undefined,
        transaction_id: transactionId || undefined,
    }), [dateFrom, dateTo, typeFilter, statusFilter, requisitesSearch, transactionId]);

    const loadHistory = useCallback(async () => {
        const version = ++requestVersion.current;
        try {
            setIsLoading(true);
            setError(null);

            const data = await paymentsAPI.getTraderHistory(currentPage, pageSize, filters);
            if (version !== requestVersion.current || latestOffset.current !== offsetMinutes) return;
            setTransactions(data.transactions);
            setTotalPages(data.pages);
            setTotalItems(data.total);
        } catch (err: any) {
            if (version !== requestVersion.current || latestOffset.current !== offsetMinutes) return;
            setTransactions([]);
            setTotalItems(0);
            setTotalPages(1);
            setError('Ошибка при загрузке истории операций');
        } finally {
            if (version === requestVersion.current) setIsLoading(false);
        }
    }, [currentPage, pageSize, filters, offsetMinutes]);

    useEffect(() => { setCurrentPage(1); }, [offsetMinutes]);

    useEffect(() => {
        loadHistory();
        return () => { requestVersion.current++; };
    }, [loadHistory]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const exportToExcel = async () => {
        if (exportingRef.current) return;
        if (dateFrom && dateTo && dateFrom > dateTo) {
            setError('Дата начала периода не может быть позже даты окончания.');
            return;
        }
        exportingRef.current = true;
        try {
            setIsExporting(true);
            setExportProgress('Загрузка…');
            setError(null);

            const statusLabels: Record<string, string> = {
                assigned: 'Назначен',
                pending_requisites: 'Ожидает реквизитов',
                receipt_uploaded: 'Чек загружен',
                confirmed: 'Подтвержден',
                completed: 'Завершен',
                cancelled: 'Отменен',
                disputed: 'Апелляция',
                no_traders: 'Нет трейдеров',
            };

            const formatExportDate = (value?: string | null) => value
                ? formatDate(value)
                : '-';
            const roundToTwoDecimals = (value?: number | null) => value == null
                ? '-'
                : Math.round((value + Number.EPSILON) * 100) / 100;

            const toRow = (transaction: PaymentTransactionResponse) => {
                const orderType = transaction.order_info?.order_type || '';
                const requisites = orderType === 'withdraw'
                    ? transaction.user_requisites
                    : transaction.order_info;
                const exchangeRate = getExchangeRate(transaction);

                return {
                    'ID транзакции': transaction.id,
                    'ID ордера': transaction.order_id || '-',
                    'Тип операции': orderType === 'deposit'
                        ? 'Пополнение'
                        : orderType === 'withdraw' ? 'Вывод' : '-',
                    'Статус': statusLabels[transaction.status] || transaction.status,
                    'Метод': getRequisiteTypeLabel(transaction.requisite_type),
                    'Клиент отдал': roundToTwoDecimals(transaction.payer_amount),
                    'Валюта отправления': transaction.payer_currency,
                    'Клиент получил': roundToTwoDecimals(transaction.receiver_amount),
                    'Валюта получения': transaction.receiver_currency,
                    'Курс (фиат за 1 USDT)': roundToTwoDecimals(exchangeRate),
                    'Комиссия трейдера, %': roundToTwoDecimals(transaction.trader_commission_rate),
                    'Заработок трейдера, USDT': roundToTwoDecimals(transaction.trader_commission_amount),
                    'Банк': bankDisplayName(requisites?.recipient_bank) || '-',
                    'Получатель': requisites?.recipient_name || '-',
                    'Телефон': requisites?.phone_number || '-',
                    'Карта / счет': requisites?.recipient_card || '-',
                    'Диплинк ElQR': orderType === 'deposit' ? transaction.order_info?.qr_url || '-' : '-',
                    'Назначен': formatExportDate(transaction.assigned_at),
                    'Чек загружен': formatExportDate(transaction.receipt_uploaded_at),
                    'Подтвержден': formatExportDate(transaction.confirmed_at),
                    'Завершен': formatExportDate(transaction.completed_at),
                    'Отменен': formatExportDate(transaction.cancelled_at),
                    'Часовой пояс': label,
                };
            };

            let exportedCount = 0;
            const worksheet = await buildTransactionExportSheet(
                async (page, size) => {
                    if (latestOffset.current !== offsetMinutes) throw new Error('Часовой пояс изменился. Повторите экспорт.');
                    const response = await paymentsAPI.getTraderHistory(page, size, filters);
                    if (latestOffset.current !== offsetMinutes) throw new Error('Часовой пояс изменился. Повторите экспорт.');
                    return { items: response.transactions, total: response.total };
                },
                toRow,
                (loaded, total) => {
                    exportedCount = loaded;
                    setExportProgress(`${loaded} / ${total}`);
                },
                { pageSize: 100, getId: transaction => transaction.id },
            );
            if (exportedCount === 0) {
                setError('По выбранным фильтрам операций нет — выгружать нечего.');
                return;
            }
            const numericColumnIndexes = new Set([5, 7, 9, 10, 11]);
            const worksheetRange = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
            for (let row = 1; row <= worksheetRange.e.r; row += 1) {
                numericColumnIndexes.forEach((column) => {
                    const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];
                    if (cell?.t === 'n') {
                        cell.z = '0.00';
                    }
                });
            }
            worksheet['!cols'] = [
                { wch: 26 }, { wch: 26 }, { wch: 16 }, { wch: 22 }, { wch: 20 },
                { wch: 16 }, { wch: 20 }, { wch: 16 }, { wch: 20 }, { wch: 24 },
                { wch: 22 }, { wch: 26 }, { wch: 22 }, { wch: 24 }, { wch: 20 },
                { wch: 24 }, { wch: 40 }, { wch: 22 }, { wch: 22 }, { wch: 22 },
                { wch: 22 }, { wch: 22 }, { wch: 14 },
            ];

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'История платежей');
            XLSX.writeFile(
                workbook,
                `trader_payment_history_${traderDateKey(Date.now(), offsetMinutes)}.xlsx`,
            );
        } catch (err) {
            setError(`Не удалось выгрузить историю операций. ${err instanceof Error ? err.message : 'Повторите попытку.'}`);
        } finally {
            exportingRef.current = false;
            setIsExporting(false);
            setExportProgress('');
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'assigned':
                return <Badge variant="secondary">Назначен</Badge>;
            case 'pending_requisites':
                return <Badge variant="outline" className="text-yellow-700 dark:text-yellow-300 border-yellow-400 bg-yellow-50 dark:bg-yellow-950">Ожидает реквизитов</Badge>;
            case 'receipt_uploaded':
                return <Badge variant="default">Чек загружен</Badge>;
            case 'confirmed':
                return <Badge className="bg-green-600">Подтвержден</Badge>;
            case 'completed':
                return <Badge className="bg-green-800">Завершен</Badge>;
            case 'no_traders':
                return <Badge variant="outline" className="text-gray-500 dark:text-slate-400 border-gray-300 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">Нет трейдеров</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const getOperationTypeIcon = (orderType: string) => {
        switch (orderType) {
            case 'deposit':
                return '💰'; // Пополнение
            case 'withdraw':
                return '📤'; // Вывод
            default:
                return '🔄';
        }
    };


    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-slate-100">История операций</h2>
                    <p className="text-gray-600 dark:text-slate-300 mt-2">Операции по вашим ордерам с учётом выбранных фильтров</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={exportToExcel}
                        disabled={isExporting}
                    >
                        {isExporting ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Download className="h-4 w-4 mr-2" />
                        )}
                        {isExporting ? `Экспорт: ${exportProgress}` : 'Экспорт в Excel'}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={loadHistory}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4" />
                        )}
                    </Button>
                </div>
            </div>

            {/* Статистика заработка с комиссий */}
            <Card className="bg-gradient-to-r from-green-50 dark:from-green-950 to-blue-50 dark:to-blue-950 border-green-200 dark:border-green-800">
                <CardHeader>
                    <CardTitle className="min-w-0 text-lg flex items-center gap-2 break-all">
                        💰 Заработок с комиссий
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-gray-500 dark:text-slate-400 mb-3">
                        Комиссии по завершённым сегодня сделкам, независимо от фильтров списка.
                        Новый день начинается в 00:00 по вашему времени.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 dark:text-slate-100">Заработано сегодня</h4>
                            <div className="text-2xl font-bold text-green-600 dark:text-green-300">
                                {commissionStats ? formatAmountWithSeparator(commissionStats.today_commission_earned, 'USDT') : '—'}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 dark:text-slate-100">За все время</h4>
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">
                                {commissionStats ? formatAmountWithSeparator(commissionStats.total_commission_earned, 'USDT') : '—'}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Фильтры */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        Фильтры
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <form className="mb-4 space-y-2" onSubmit={event => {
                        event.preventDefault();
                        const id = transactionIdInput.trim();
                        setCurrentPage(1);
                        const requisites = requisitesInput.trim();
                        setTransactionId(id);
                        setRequisitesSearch(requisites);
                        if (id === transactionId && requisites === requisitesSearch && currentPage === 1) loadHistory();
                    }}>
                        <Label htmlFor="transactionId">ID сделки</Label>
                        <div className="flex flex-wrap gap-2">
                            <Input id="transactionId" value={transactionIdInput}
                                onChange={event => setTransactionIdInput(event.target.value)}
                                placeholder="Введите полный ID сделки" className="flex-1 min-w-0" />

                        </div>
                        <Label htmlFor="historyRequisites">Карта, телефон или счёт</Label>
                        <div className="flex flex-wrap gap-2">
                            <Input id="historyRequisites" value={requisitesInput}
                                onChange={event => setRequisitesInput(event.target.value)}
                                placeholder="Номер целиком или его часть" className="flex-1 min-w-0" />
                            <Button type="submit">Найти</Button>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-slate-400">Можно искать по ID, реквизиту или обоим полям. Пробелы, скобки и дефисы в номере не мешают поиску. Учитываются фильтры ниже.</p>
                    </form>
                    <p className="mb-3 text-sm text-gray-500 dark:text-slate-400">
                        Период — по времени назначения сделки ({label}). Excel учитывает все выбранные фильтры.
                        Без дат выгружается вся история, соответствующая остальным фильтрам.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="dateFrom">Дата назначения от</Label>
                            <Input
                                id="dateFrom"
                                type="date"
                                value={dateFrom}
                                onChange={(e) => { setCurrentPage(1); setDateFrom(e.target.value); }}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="dateTo">Дата назначения до</Label>
                            <Input
                                id="dateTo"
                                type="date"
                                value={dateTo}
                                onChange={(e) => { setCurrentPage(1); setDateTo(e.target.value); }}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="typeFilter">Тип платежа</Label>
                            <select
                                id="typeFilter"
                                value={typeFilter}
                                onChange={(e) => { setCurrentPage(1); setTypeFilter(e.target.value); }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800"
                            >
                                <option value="all">Все операции</option>
                                <option value="deposit">Пополнение</option>
                                <option value="withdraw">Вывод</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="statusFilter">Статус</Label>
                            <select
                                id="statusFilter"
                                value={statusFilter}
                                onChange={(e) => { setCurrentPage(1); setStatusFilter(e.target.value); }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800"
                            >
                                <option value="all">Все статусы</option>
                                <option value="completed">Успех</option>
                                <option value="cancelled">Отмена</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setCurrentPage(1);
                                setTransactionIdInput('');
                                setTransactionId('');
                                setRequisitesInput('');
                                setRequisitesSearch('');
                                setDateFrom('');
                                setDateTo('');
                                setTypeFilter('all');
                                setStatusFilter('all');
                            }}
                        >
                            Сбросить фильтры
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {error && (
                <div className="p-4 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-300" />
                    <span className="ml-2 text-gray-600 dark:text-slate-300">Загружаем историю операций...</span>
                </div>
            ) : transactions.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <Calendar className="h-16 w-16 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">{transactionId || requisitesSearch ? 'Сделка не найдена' : 'История пуста'}</h3>
                        <p className="text-gray-500 dark:text-slate-400 text-center">
                            {transactionId || requisitesSearch ? 'Среди ваших сделок с выбранными фильтрами совпадений нет.' : 'Здесь будут отображаться все завершенные операции по вашим ордерам'}
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="grid gap-6">
                        {transactions.map((transaction) => (
                            <Card key={transaction.id} className="border border-gray-200 dark:border-slate-700 hover:shadow-md transition-shadow">
                                <CardHeader>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <CardTitle className="min-w-0 text-lg flex items-center gap-2 break-all">
                                            <span>{getOperationTypeIcon(transaction.order_info?.order_type || '')}</span>
                                            <span>
                                                {transaction.order_info?.order_type === 'deposit' ? 'Пополнение' : 'Вывод'} #{transaction.id}
                                            </span>
                                        </CardTitle>
                                        {getStatusBadge(transaction.status)}
                                    </div>
                                    <CardDescription className="flex flex-wrap items-center gap-4">
                                        <span>Создано: {formatDate(transaction.assigned_at)}</span>
                                        {transaction.completed_at && (
                                            <span>Завершено: {formatDate(transaction.completed_at)}</span>
                                        )}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {transaction.confirmation_method === 'automatic' && <div className="rounded border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950 p-3 text-sm">
                                        <strong>Подтверждено автоматически</strong>
                                    </div>}

                                    {/* Информация об операции */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <h4 className="font-medium text-gray-900 dark:text-slate-100">💱 Обмен</h4>
                                            <div className="text-sm space-y-1">
                                                <div className="flex items-center justify_between">
                                                    <span className="text-gray-600 dark:text-slate-300">Клиент отдал:</span>
                                                    <span className="font-medium">{formatAmount(transaction.payer_amount, transaction.payer_currency)}</span>
                                                </div>
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <span className="text-gray-600 dark:text-slate-300">Клиент получил:</span>
                                                    <span className="font-medium">{formatAmount(transaction.receiver_amount, transaction.receiver_currency)}</span>
                                                </div>
                                                {/* Курс обмена на момент создания */}
                                                {(() => {
                                                    const exchangeRate = getExchangeRate(transaction);
                                                    return exchangeRate && (
                                                        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-1 mt-1">
                                                            <span className="text-gray-600 dark:text-slate-300 text-xs">Курс обмена:</span>
                                                            <span className="font-medium text-blue-600 dark:text-blue-300 text-xs">
                                                                1 USDT = {formatAmount(exchangeRate)} {transaction.payer_currency === 'USDT' ? transaction.receiver_currency : transaction.payer_currency}
                                                            </span>
                                                        </div>
                                                    );
                                                })()}
                                                {/* Информация о комиссии */}
                                                {transaction.trader_commission_amount && transaction.trader_commission_rate && (
                                                    <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-1 mt-2">
                                                        <span className="text-green-600 dark:text-green-300 font-medium">💰 Ваш заработок:</span>
                                                        <span className="font-bold text-green-600 dark:text-green-300">
                                                            {formatAmountWithSeparator(transaction.trader_commission_amount, 'USDT')}
                                                        </span>
                                                    </div>
                                                )}
                                                {transaction.trader_commission_rate && (
                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                        <span className="text-gray-500 dark:text-slate-400 text-xs">Комиссия:</span>
                                                        <span className="text-gray-500 dark:text-slate-400 text-xs">{transaction.trader_commission_rate}%</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Информация об ордере */}
                                        {transaction.order_info && (
                                            <div className="space-y-2">
                                                <h4 className="font-medium text-gray-900 dark:text-slate-100">📋 Ваш ордер</h4>
                                                <div className="text-sm space-y-1">
                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                        <span className="text-gray-600 dark:text-slate-300">Курс:</span>
                                                        <span>{formatAmount(transaction.order_info.from_amount, transaction.order_info.from_currency)} → {formatAmount(transaction.order_info.to_amount, transaction.order_info.to_currency)}</span>
                                                    </div>
                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                        <span className="text-gray-600 dark:text-slate-300">Метод:</span>
                                                        <span className="font-medium">{getRequisiteTypeLabel(transaction.requisite_type)}</span>
                                                    </div>
                                                    {/* Реквизиты трейдера для пополнения */}
                                                    {transaction.order_info.order_type === 'deposit' && (
                                                        <>
                                                            {transaction.order_info.recipient_bank && (
                                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                                    <span className="text-gray-600 dark:text-slate-300">Банк:</span>
                                                                    <span className="font-medium text-xs">{bankDisplayName(transaction.order_info.recipient_bank)}</span>
                                                                </div>
                                                            )}
                                                            {transaction.order_info.recipient_name && (
                                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                                    <span className="text-gray-600 dark:text-slate-300">Получатель:</span>
                                                                    <span className="font-medium text-xs">{transaction.order_info.recipient_name}</span>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    {transaction.order_info.order_type === 'deposit' && transaction.order_info.recipient_card && (
                                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                                            <span className="text-gray-600 dark:text-slate-300">Карта:</span>
                                                            <span className="font-medium text-xs font-mono">{formatCardNumber(transaction.order_info.recipient_card)}</span>
                                                        </div>
                                                    )}
                                                    {/* Реквизиты клиента для вывода */}
                                                    {transaction.order_info.order_type === 'withdraw' && transaction.user_requisites && (
                                                        <>
                                                            {transaction.user_requisites.recipient_bank && (
                                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                                    <span className="text-gray-600 dark:text-slate-300">Банк клиента:</span>
                                                                    <span className="font-medium text-xs">{bankDisplayName(transaction.user_requisites.recipient_bank)}</span>
                                                                </div>
                                                            )}
                                                            {transaction.user_requisites.recipient_name && (
                                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                                    <span className="text-gray-600 dark:text-slate-300">Получатель:</span>
                                                                    <span className="font-medium text-xs">{transaction.user_requisites.recipient_name}</span>
                                                                </div>
                                                            )}
                                                            {transaction.user_requisites.phone_number && (
                                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                                    <span className="text-gray-600 dark:text-slate-300">Телефон:</span>
                                                                    <span className="font-medium text-xs">{transaction.user_requisites.phone_number}</span>
                                                                </div>
                                                            )}
                                                            {transaction.user_requisites.recipient_card && (
                                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                                    <span className="text-gray-600 dark:text-slate-300">Карта:</span>
                                                                    <span className="font-medium text-xs font-mono">{formatCardNumber(transaction.user_requisites.recipient_card)}</span>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Временные метки */}
                                        <div className="space-y-2">
                                            <h4 className="font-medium text-gray-900 dark:text-slate-100">⏱️ Временная линия</h4>
                                            <div className="text-sm space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <CheckCircle className="h-3 w-3 text-green-600 dark:text-green-300" />
                                                    <span className="text-gray-600 dark:text-slate-300">Назначен: {formatDate(transaction.assigned_at)}</span>
                                                </div>
                                                {transaction.receipt_uploaded_at && (
                                                    <div className="flex items-center gap-2">
                                                        <CheckCircle className="h-3 w-3 text-blue-600 dark:text-blue-300" />
                                                        <span className="text-gray-600 dark:text-slate-300">Чек: {formatDate(transaction.receipt_uploaded_at)}</span>
                                                    </div>
                                                )}
                                                {transaction.confirmed_at && (
                                                    <div className="flex items-center gap-2">
                                                        <CheckCircle className="h-3 w-3 text-purple-600 dark:text-purple-300" />
                                                        <span className="text-gray-600 dark:text-slate-300">Подтвержден: {formatDate(transaction.confirmed_at)}</span>
                                                    </div>
                                                )}
                                                {transaction.completed_at && (
                                                    <div className="flex items-center gap-2">
                                                        <CheckCircle className="h-3 w-3 text-green-800 dark:text-green-200" />
                                                        <span className="text-gray-600 dark:text-slate-300">Завершен: {formatDate(transaction.completed_at)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <WithdrawReceiptDownload transactionId={transaction.id} receipts={transaction.withdraw_receipts} filename={transaction.withdraw_receipt_filename} />
                                    {/* Дополнительная информация */}
                                    {(transaction.receipt_filename || transaction.notes) && (
                                        <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {transaction.receipt_filename && (
                                                    <div className="space-y-2">
                                                        <h4 className="font-medium text-gray-900 dark:text-slate-100">📎 Чек</h4>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-gray-600 dark:text-slate-300">{transaction.receipt_filename}</span>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => window.open(paymentsAPI.getReceipt(transaction.id), '_blank')}
                                                            >
                                                                <ExternalLink className="h-3 w-3 mr-1" />
                                                                Открыть
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}

                                                {transaction.notes && (
                                                    <div className="space-y-2">
                                                        <h4 className="font-medium text-gray-900 dark:text-slate-100">📝 Примечания</h4>
                                                        <p className="text-sm text-gray-600 dark:text-slate-300 italic">{transaction.notes}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Итоговая информация */}
                                    <div className="bg-gray-50 dark:bg-slate-900 p-3 rounded-md">
                                        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                                            <span className="text-gray-600 dark:text-slate-300">ID транзакции:</span>
                                            <span className="font-mono text-xs">{transaction.id}</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </>
            )}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={totalItems}
                pageSize={pageSize}
                disabled={isLoading}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                }}
            />
        </div>
    );
};
